import type { AxiosPromise, RawAxiosRequestConfig } from 'axios'
import type { DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { useUserStore } from './user'
import type {
  GETAppointment,
  GETAppointmentStatusCount,
  TransactionDirection,
} from '~/services/apiClient'
import { handleSkippedAPIError } from '~/apiErrorHandling'
import { useGlobalFilters } from '~/compositions/globalFilters'
import { NO_RESPONSE_RETRY_MS, retryOnNoResponse } from '~/noResponseRetry'
import { isRequestAbort } from '~/requestAbort'
import { Appointment } from '~/models/appointments'
import {
  AppointmentsApi,
  AppointmentStatus,
  TerminalName,
} from '~/services/apiClient'

export const useAppointmentStore = defineStore('appointments', {
  state: () => {
    return {
      counts: [] as GETAppointmentStatusCount[],
      loading: false,
      appointments: [] as Appointment[],
      // The request currently allowed to write `appointments`. Both load actions
      // share that one list, so whichever started last owns it.
      currentRequest: null as AbortController | null,
    }
  },
  actions: {
    load({
      container_numbers,
      after,
      statuses,
      direction,
      loaded,
    }: {
      container_numbers: string[]
      after?: DateTime
      statuses?: AppointmentStatus[]
      direction?: TransactionDirection | null
      loaded?: boolean | null
    }): Promise<void> {
      const request = this.startRequest()
      return fetchContainerAppointments({
        container_numbers,
        after,
        statuses,
        direction,
        loaded,
        demo_mode: useUserStore().demo_mode,
        signal: request.signal,
      })
        .then((appointments) => {
          if (this.currentRequest === request) {
            this.appointments = appointments
          }
        })
        .catch((error) => rethrowUnlessStopped(error, request.signal))
        .finally(() => this.finishRequest(request))
    },
    load_with_options(options: {
      terminal?: TerminalName
      after?: DateTime
      before?: DateTime
      loaded?: boolean
      direction?: TransactionDirection
      page?: number
      pageSize?: number
      statuses?: AppointmentStatus[]
    }): Promise<void> {
      const userStore = useUserStore()
      const portComplexes = useGlobalFilters().portComplexes.value
      const request = this.startRequest()
      const api = new AppointmentsApi()
      return listAppointments(request.signal, (config) =>
        api.listAppointmentsAppointmentsGet(
          options.terminal,
          undefined,
          options.after?.toISO(),
          options.before?.toISO(),
          options.direction,
          options.loaded,
          options.statuses,
          options.page,
          options.pageSize,
          portComplexes,
          undefined,
          config
        )
      )
        .then((rawAppointments) => {
          if (this.currentRequest === request) {
            this.appointments = sortAppointmentsNewestFirst(
              rawAppointments.map(
                (rawAppt) => new Appointment(rawAppt, userStore.demo_mode)
              )
            )
          }
        })
        .catch((error) => rethrowUnlessStopped(error, request.signal))
        .finally(() => this.finishRequest(request))
    },
    /** Supersedes any request still in flight and clears the previous result. */
    startRequest() {
      this.currentRequest?.abort()
      const request = new AbortController()
      this.currentRequest = request
      this.appointments = []
      this.loading = true
      return request
    },
    finishRequest(request: AbortController) {
      // A superseded request must not end the loading state of its successor.
      if (this.currentRequest === request) {
        this.currentRequest = null
        this.loading = false
      }
    },
  },
})

/**
 * A superseded or abandoned request is no longer anyone's business. After a
 * retry backoff the error can be the original network failure rather than a
 * cancel, so the signal is checked too.
 */
function rethrowUnlessStopped(error: unknown, signal: AbortSignal) {
  if (!isRequestAbort(error) && !signal.aborted) {
    throw error
  }
}

/**
 * Every `/appointments/` read goes through here. A dropped connection gets one
 * retry, and the toast and 401 logout that `skipErrorHandling` holds back are
 * replayed once it is really over, so the first drop doesn't toast when the
 * retry works.
 */
async function listAppointments(
  signal: AbortSignal,
  call: (config: RawAxiosRequestConfig) => AxiosPromise<GETAppointment[]>
): Promise<GETAppointment[]> {
  const config = { signal, skipErrorHandling: true }
  try {
    const resp = await retryOnNoResponse(() => call(config), {
      extraAttempts: 1,
      delayMs: () => NO_RESPONSE_RETRY_MS,
      signal,
    })
    return resp.data
  } catch (error) {
    if (isRequestAbort(error) || signal.aborted) {
      throw error
    }
    // Logged out: nothing to show and nothing to report.
    if (await handleSkippedAPIError(error)) {
      return []
    }
    throw error
  }
}

/**
 * Newest window first; ties go to whichever appointment the terminal created
 * (or we observed) last.
 */
export function sortAppointmentsNewestFirst(appointments: Appointment[]) {
  return appointments.sort((a, b) => {
    const windowDiff = b.window_start.toMillis() - a.window_start.toMillis()
    if (windowDiff === 0) {
      return (
        (b.created_at_terminal || b.last_observed_changed).toMillis() -
        (a.created_at_terminal || a.last_observed_changed).toMillis()
      )
    }
    return windowDiff
  })
}

const CONTAINER_APPOINTMENTS_PAGE_SIZE = 25

/** One container's appointments, parsed and sorted for display. */
export async function fetchContainerAppointments({
  container_numbers,
  after,
  statuses,
  direction,
  loaded,
  demo_mode,
  signal,
}: {
  container_numbers: string[]
  after?: DateTime
  statuses?: AppointmentStatus[]
  direction?: TransactionDirection | null
  loaded?: boolean | null
  demo_mode: boolean
  signal: AbortSignal
}) {
  const api = new AppointmentsApi()
  const rawAppointments = await listAppointments(signal, (config) =>
    api.listAppointmentsAppointmentsGet(
      undefined,
      undefined,
      after ? after.toISO() : undefined,
      undefined,
      direction ?? undefined,
      loaded ?? undefined,
      statuses,
      1,
      CONTAINER_APPOINTMENTS_PAGE_SIZE,
      undefined,
      container_numbers.join(','),
      config
    )
  )
  return sortAppointmentsNewestFirst(
    parseAppointments(rawAppointments, demo_mode)
  )
}

export function parseAppointments(
  rawAppointments: GETAppointment[],
  demo_mode: boolean
) {
  const appointments = rawAppointments
    .filter(
      // if its apm and scheduling error, ignore it
      (rawAppt) => {
        return !(
          rawAppt.terminal === TerminalName.Apm &&
          rawAppt.status === AppointmentStatus.SchedulingError
        )
      }
    )
    .map((rawAppt) => {
      const appt = new Appointment(rawAppt, demo_mode)
      return appt
    })
  return appointments
}
